// Reusable field renderer used by BOTH the live preview pane in the
// Form Builder AND the public portal form page. Keeping a single renderer
// guarantees admins see exactly what their customers will fill in.
//
// Props:
//   field    — sanitized field schema {id, key, label, type, required, ...}
//   value    — current value (string, array for multiselect, bool for checkbox)
//   onChange — (newValue) => void
//   disabled — disables interaction (used in the builder preview)
import React from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import RichEditor from '../../RichEditor';

const PRIORITY_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
  { label: 'Urgent', value: 'urgent' },
];

export default function PortalField({ field, value, onChange, disabled = false }) {
  const setVal = (v) => !disabled && onChange?.(v);
  const id = `pf-${field.id || field.key}`;

  const labelEl = (
    <Label htmlFor={id} className="text-xs">
      {field.label}{field.required && ' *'}
    </Label>
  );
  const helpEl = field.help_text ? <p className="text-[11px] text-zinc-500 mt-1">{field.help_text}</p> : null;

  switch (field.type) {
    case 'textarea':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Textarea id={id} rows={4} value={value || ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder || ''}
            maxLength={field.max_length || undefined} />
          {helpEl}
        </div>
      );

    case 'number':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} type="number" value={value ?? ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder || ''}
            min={Number.isFinite(field.min) ? field.min : 0} max={field.max} />
          {helpEl}
        </div>
      );

    case 'email':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} type="email" value={value || ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder || 'name@company.com'} />
          {helpEl}
        </div>
      );

    case 'url':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} type="url" value={value || ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder || 'https://…'} />
          {helpEl}
        </div>
      );

    case 'date':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} type="date" value={value || ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} />
          {helpEl}
        </div>
      );

    case 'select':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Select value={value || ''} onValueChange={(v) => setVal(v)} disabled={disabled}>
            <SelectTrigger id={id}><SelectValue placeholder={field.placeholder || 'Pick one…'} /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helpEl}
        </div>
      );

    case 'priority': {
      const opts = (field.options && field.options.length) ? field.options : PRIORITY_OPTIONS;
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Select value={value || ''} onValueChange={(v) => setVal(v)} disabled={disabled}>
            <SelectTrigger id={id}><SelectValue placeholder={field.placeholder || 'Select priority…'} /></SelectTrigger>
            <SelectContent>
              {opts.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helpEl}
        </div>
      );
    }

    case 'richtext':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          {disabled ? (
            <div className="border border-zinc-200 rounded-lg p-3 text-sm text-zinc-400 bg-zinc-50 mt-1">
              Rich text editor — bold, highlight, links…
            </div>
          ) : (
            <div className="mt-1">
              <RichEditor value={value || ''} onChange={(v) => setVal(v)}
                placeholder={field.placeholder || 'Type here… use the toolbar to format'} minHeight={120} />
            </div>
          )}
          {helpEl}
        </div>
      );

    case 'multiselect': {
      // Checkbox group — easier to scan than a multi-select dropdown on
      // a customer-facing form, and works without extra UI deps.
      const arr = Array.isArray(value) ? value : (value ? String(value).split(',') : []);
      const toggle = (v) => setVal(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <div className="space-y-1 mt-1 border border-zinc-200 rounded-md p-2">
            {(field.options || []).map(o => (
              <label key={o.value} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input type="checkbox" checked={arr.includes(o.value)} onChange={() => toggle(o.value)} disabled={disabled} />
                <span>{o.label || o.value}</span>
              </label>
            ))}
          </div>
          {helpEl}
        </div>
      );
    }

    case 'radio':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <div className="space-y-1 mt-1">
            {(field.options || []).map(o => (
              <label key={o.value} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input type="radio" name={id} value={o.value} checked={value === o.value}
                  onChange={(e) => setVal(e.target.value)} disabled={disabled} />
                <span>{o.label || o.value}</span>
              </label>
            ))}
          </div>
          {helpEl}
        </div>
      );

    case 'checkbox':
      return (
        <div data-testid={`portal-field-${field.key}`}>
          <label className="flex items-start gap-2 cursor-pointer">
            <input id={id} type="checkbox" checked={!!value} disabled={disabled}
              onChange={(e) => setVal(e.target.checked)} className="mt-0.5" />
            <span className="text-sm">{field.label}{field.required && ' *'}</span>
          </label>
          {helpEl}
        </div>
      );

    case 'file':
      // Note: file uploads on the portal go through a separate attachment
      // endpoint AFTER the ticket is created. Here we just collect the
      // file name in form_data — the actual upload is wired by the
      // request-form page after submit returns the new ticket_key.
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} type="file" onChange={(e) => {
            const f = e.target.files?.[0];
            setVal(f ? { name: f.name, size: f.size, _file: f } : null);
          }} disabled={disabled} />
          {value?.name && <p className="text-[11px] text-zinc-500 mt-1">{value.name}</p>}
          {helpEl}
        </div>
      );

    case 'text':
    default:
      return (
        <div data-testid={`portal-field-${field.key}`}>
          {labelEl}
          <Input id={id} value={value || ''} disabled={disabled}
            onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder || ''}
            maxLength={field.max_length || undefined} />
          {helpEl}
        </div>
      );
  }
}
